/**
 * Endpoint defs for the availability-import capability (availability-import).
 *
 * `upload`/`preview` landed in task 2.1; `confirm` is added by this task
 * (2.2) — per `packages/contracts/AGENTS.md`'s four-step rule, endpoint defs
 * land in the same task that adds the NestJS route.
 *
 * Traceability: availability-import-pipeline / Requirement: Separate entry
 * point reusing import_jobs, Upload and parse, Profile resolution and
 * preview, Confirm applies an edited profile and enqueues the worker job.
 */
import { z } from 'zod';
import { endpoint } from './endpoint.js';
import {
  AvailabilityImportUploadResponseSchema,
  AvailabilityImportPreviewResponseSchema,
  AvailabilityImportRematchRequestSchema,
  AvailabilityImportRematchResponseSchema,
  AvailabilityImportConfirmRequestSchema,
  AvailabilityImportConfirmResponseSchema,
  AvailabilityImportStatusResponseSchema,
  AvailabilityImportHeaderRowIndexSchema,
} from '../schemas/availability-import.js';
import { ImportErrorsResponseSchema } from '../schemas/import-job.js';

export const availabilityImportEndpoints = {
  /** POST /api/availability-import/upload — upload XLSX, parse headers/sample rows, create import_jobs record */
  upload: endpoint(
    'POST',
    '/api/availability-import/upload',
    z.object({}),
    AvailabilityImportUploadResponseSchema,
  ),

  /** GET /api/availability-import/:id/preview — resolve profile + row matches, no writes.
   * `headerRowIndex` (optional): operator's manual header-row override, sent as a query
   * param — see gap 1, CU-86cb1npz2. Omitted falls back to detectHeaderRow's own auto-detection. */
  preview: endpoint(
    'GET',
    '/api/availability-import/:id/preview',
    AvailabilityImportHeaderRowIndexSchema.extend({ id: z.coerce.number() }),
    AvailabilityImportPreviewResponseSchema,
  ),

  /** POST /api/availability-import/:id/rematch — recompute matchedUnitId/matchMethod against an edited (unconfirmed) profile, no writes */
  rematch: endpoint(
    'POST',
    '/api/availability-import/:id/rematch',
    AvailabilityImportRematchRequestSchema.extend({ id: z.coerce.number() }),
    AvailabilityImportRematchResponseSchema,
  ),

  /** POST /api/availability-import/:id/resolve-fresh — bypass the saved-mapping signature lookup and re-run tiers 2-3, no writes.
   * `headerRowIndex` (optional): operator's manual header-row override — this is a POST
   * endpoint, so the typed client sends it as a JSON body field, not a query param (see
   * gap 1, CU-86cb1npz2). Omitted falls back to detectHeaderRow's own auto-detection. */
  resolveFresh: endpoint(
    'POST',
    '/api/availability-import/:id/resolve-fresh',
    AvailabilityImportHeaderRowIndexSchema.extend({ id: z.coerce.number() }),
    AvailabilityImportPreviewResponseSchema,
  ),

  /** POST /api/availability-import/:id/confirm — persist the (possibly edited) profile, claim the job, and enqueue the worker job */
  confirm: endpoint(
    'POST',
    '/api/availability-import/:id/confirm',
    AvailabilityImportConfirmRequestSchema.extend({ id: z.coerce.number() }),
    AvailabilityImportConfirmResponseSchema,
  ),

  /** GET /api/availability-import/:id — poll status + result counts (task 4.3) */
  status: endpoint(
    'GET',
    '/api/availability-import/:id',
    z.object({ id: z.coerce.number() }),
    AvailabilityImportStatusResponseSchema,
  ),

  /** GET /api/availability-import/:id/errors — unmatched-row report (task 4.3) */
  errors: endpoint(
    'GET',
    '/api/availability-import/:id/errors',
    z.object({ id: z.coerce.number() }),
    ImportErrorsResponseSchema,
  ),
};
